import React, {createContext, useContext, useEffect, useState} from 'react'
import {ThemeModeComponent} from '../../../assets/ts/layout'
import {toAbsoluteUrl} from '../../../helpers'

export type ThemeModeType = 'dark' | 'light' | 'system'
export const themeModelSKey = 'kt_theme_mode_value'
export const themeMenuModeLSKey = 'kt_theme_mode_menu'

type ResolvedThemeMode = 'light' | 'dark'

type ThemeModeContextType = {
  mode: ThemeModeType
  systemMode: ResolvedThemeMode
  menuMode: ThemeModeType
  updateMode: (_mode: ThemeModeType) => void
  updateMenuMode: (_mode: ThemeModeType) => void
}

const themeModeSwitchHelper = (_mode: ThemeModeType) => {
  // change background image url
  const mode = _mode !== 'system' ? _mode : ThemeModeComponent.getSystemMode()
  const imageUrl = '/media/patterns/header-bg' + (mode === 'light' ? '.jpg' : '-dark.png')
  document.body.style.backgroundImage = `url("${toAbsoluteUrl(imageUrl)}")`
}

const getThemeModeFromLocalStorage = (lsKey: string): ThemeModeType => {
  const data = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(lsKey) : null
  if (data === 'dark' || data === 'light' || data === 'system') {
    return data
  }
  return 'system'
}

const getSavedThemePreference = (): ThemeModeType => {
  const menuPreference = getThemeModeFromLocalStorage(themeMenuModeLSKey)
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(themeMenuModeLSKey)) {
    return menuPreference
  }
  return getThemeModeFromLocalStorage(themeModelSKey)
}

const defaultThemeMode: ThemeModeContextType = {
  mode: getSavedThemePreference(),
  systemMode: ThemeModeComponent.getSystemMode() as ResolvedThemeMode,
  menuMode: getSavedThemePreference(),
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultThemeMode.mode,
  systemMode: defaultThemeMode.systemMode,
  menuMode: defaultThemeMode.menuMode,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
})

const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({children}: {children: React.ReactNode}) => {
  const [mode, setMode] = useState<ThemeModeType>(defaultThemeMode.mode)
  const [systemMode, setSystemMode] = useState<ResolvedThemeMode>(defaultThemeMode.systemMode)
  const [menuMode, setMenuMode] = useState<ThemeModeType>(defaultThemeMode.menuMode)

  const updateMode = (_mode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    setMode(_mode)
    if (saveInLocalStorage) {
      setMenuMode(_mode)
    }
    if (saveInLocalStorage && sessionStorage) {
      sessionStorage.setItem(themeModelSKey, _mode)
      sessionStorage.setItem(themeMenuModeLSKey, _mode)
    }

    const resolvedMode = _mode === 'system' ? ThemeModeComponent.getSystemMode() : _mode
    document.documentElement.setAttribute('data-bs-theme', resolvedMode)
  }

  const updateMenuMode = (_menuMode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    setMenuMode(_menuMode)
    if (saveInLocalStorage && sessionStorage) {
      sessionStorage.setItem(themeMenuModeLSKey, _menuMode)
    }
  }

  useEffect(() => {
    updateMode(mode, false)
    updateMenuMode(menuMode, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = () => {
      const nextSystemMode: ResolvedThemeMode = mediaQuery.matches ? 'dark' : 'light'
      setSystemMode(nextSystemMode)
      if (mode === 'system') {
        document.documentElement.setAttribute('data-bs-theme', nextSystemMode)
      }
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange)
    } else {
      mediaQuery.addListener(handleSystemThemeChange)
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange)
      } else {
        mediaQuery.removeListener(handleSystemThemeChange)
      }
    }
  }, [mode])

  return (
    <ThemeModeContext.Provider value={{mode, systemMode, menuMode, updateMode, updateMenuMode}}>
      {children}
    </ThemeModeContext.Provider>
  )
}

export {ThemeModeProvider, themeModeSwitchHelper, useThemeMode}
